import { ALPHABET, normalizeLetterPool } from './shopOptions';
import { SHOP_RARITY_CHANCES } from './shopConfig';
import { normalizeShopRarityChances, SHOP_CHOICE_COUNT } from '../engine/shop';
import { GAME_CONFIG } from './gameConfig';

const createDefaultRunSettings = () => ({
  economy: {
    baseSubmitsPerRound: Math.max(1, Number(GAME_CONFIG.roundEconomy?.submitsPerRound) || 4),
    baseDiscardsPerRound: Math.max(0, Number(GAME_CONFIG.roundEconomy?.discardsPerRound) || 2),
    baseShopPicksPerStage: 5,
    baseShopRerollsPerStage: 1,
    shopOptionCount: Math.max(1, Number(SHOP_CHOICE_COUNT) || 3),
  },
  shop: {
    rarityChances: normalizeShopRarityChances(SHOP_RARITY_CHANCES),
  },
  persistentEffects: {
    letterXPlusOne: {
      letterPool: ALPHABET,
      bonus: 2,
      scope: 'game',
    },
  },
});

export const DEFAULT_RUN_SETTINGS = createDefaultRunSettings();

export const normalizeRunSettings = (runSettings) => {
  const defaults = DEFAULT_RUN_SETTINGS;
  const economy = runSettings?.economy || {};
  const shop = runSettings?.shop || {};
  const persistentEffects = runSettings?.persistentEffects || {};
  const legacyRarityChances = runSettings?.economy?.shopRarityChances;
  const normalizedRarityChances = normalizeShopRarityChances(
    shop.rarityChances || legacyRarityChances || defaults.shop.rarityChances
  );

  return {
    economy: {
      baseSubmitsPerRound: Math.max(
        1,
        Number(economy.baseSubmitsPerRound ?? defaults.economy.baseSubmitsPerRound) ||
          defaults.economy.baseSubmitsPerRound
      ),
      baseDiscardsPerRound: Math.max(
        0,
        Number(economy.baseDiscardsPerRound ?? defaults.economy.baseDiscardsPerRound) ||
          defaults.economy.baseDiscardsPerRound
      ),
      baseShopPicksPerStage: Math.max(
        1,
        Number(economy.baseShopPicksPerStage ?? defaults.economy.baseShopPicksPerStage) ||
          defaults.economy.baseShopPicksPerStage
      ),
      baseShopRerollsPerStage: Math.max(
        0,
        Number(economy.baseShopRerollsPerStage ?? defaults.economy.baseShopRerollsPerStage) ||
          defaults.economy.baseShopRerollsPerStage
      ),
      shopOptionCount: Math.max(
        1,
        Number(economy.shopOptionCount ?? defaults.economy.shopOptionCount) ||
          defaults.economy.shopOptionCount
      ),
    },
    shop: {
      rarityChances: normalizedRarityChances,
    },
    persistentEffects: {
      letterXPlusOne: {
        letterPool: normalizeLetterPool(
          persistentEffects?.letterXPlusOne?.letterPool,
          defaults.persistentEffects.letterXPlusOne.letterPool
        ),
        bonus: Number.isFinite(Number(persistentEffects?.letterXPlusOne?.bonus))
          ? Number(persistentEffects.letterXPlusOne.bonus)
          : defaults.persistentEffects.letterXPlusOne.bonus,
        scope:
          String(
            persistentEffects?.letterXPlusOne?.scope ||
              defaults.persistentEffects.letterXPlusOne.scope
          ) || defaults.persistentEffects.letterXPlusOne.scope,
      },
    },
  };
};

export const getRoundSubmits = (runSettings) =>
  Math.max(
    1,
    Number(runSettings?.economy?.baseSubmitsPerRound) ||
      DEFAULT_RUN_SETTINGS.economy.baseSubmitsPerRound
  );

export const getRoundDiscards = (runSettings) =>
  Math.max(
    0,
    Number(runSettings?.economy?.baseDiscardsPerRound) ||
      DEFAULT_RUN_SETTINGS.economy.baseDiscardsPerRound
  );

export const getBaseShopPicksPerStage = (runSettings) =>
  Math.max(
    1,
    Number(runSettings?.economy?.baseShopPicksPerStage) ||
      DEFAULT_RUN_SETTINGS.economy.baseShopPicksPerStage
  );

export const getBaseShopRerollsPerStage = (runSettings) =>
  Math.max(
    0,
    Number(runSettings?.economy?.baseShopRerollsPerStage) ||
      DEFAULT_RUN_SETTINGS.economy.baseShopRerollsPerStage
  );

export const getShopOptionCount = (runSettings) =>
  Math.max(
    1,
    Number(runSettings?.economy?.shopOptionCount) ||
      DEFAULT_RUN_SETTINGS.economy.shopOptionCount
  );

export const getShopRarityChances = (runSettings) =>
  normalizeShopRarityChances(
    runSettings?.shop?.rarityChances || DEFAULT_RUN_SETTINGS.shop.rarityChances
  );
